import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient, HttpResponse} from '@angular/common/http';
import {environment} from '@env/environment';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {catchError, finalize, tap} from 'rxjs/operators';
import {getHttpParams} from '@shared/common-functions.util';

export type TPdfConverterConversionStatus =
  'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'EXPIRED';

export type TPdfConverterExportFormat = 'xls' | 'xlsx' | 'csv' | 'ofx' | 'qbo';

export type TPdfConverterValidationStatus =
  'BALANCED'
  | 'BALANCE_MISMATCH'
  | 'TOTALS_MISMATCH'
  | 'BALANCE_MISSING'
  | 'NO_TRANSACTIONS';

export interface IPdfConverterConversion {
  id: number;
  originalFilename: string;
  status: TPdfConverterConversionStatus;
  createdAt: string;
  startedAt: string | null;
  completedAt: string | null;
  pageCount: number | null;
  transactionCount: number | null;
  validated: boolean | null;
  validationMessage: string | null;
  // Absent on conversions completed before the backend started reporting it; the UI goes by `validated`.
  validationStatus?: TPdfConverterValidationStatus;
  errorMessage: string | null;
}

export interface IPdfConverterConversionsRequest {
  status?: TPdfConverterConversionStatus[];
  sort?: keyof IPdfConverterConversion | string;
  direction?: 'asc' | 'desc' | string;
  page?: number;
  rowsPerPage?: string | number;
}

@Injectable({
  providedIn: 'root',
})
export class PdfConverterService {
  conversionChanged = new EventEmitter<IPdfConverterConversion>();
  conversionDeleted = new EventEmitter<number>();
  private readonly activeConversionsSubject = new BehaviorSubject<IPdfConverterConversion[]>([]);
  readonly activeConversions$ = this.activeConversionsSubject.asObservable();
  private readonly activeStatus: TPdfConverterConversionStatus = 'PROCESSING';
  private readonly queue: number[] = [];
  private readonly conversionMap = new Map<number, IPdfConverterConversion>();
  private readonly pendingActionsMap = new Map<number, boolean>();

  private queueInterval: ReturnType<typeof setInterval> | null = null;
  private statusIsUpdating: boolean = false;

  constructor(
    private http: HttpClient,
  ) {
    this.startQueue();
  }

  createConversion(file: File): Observable<IPdfConverterConversion> {
    const data = new FormData();
    data.append('file', file);
    return this.http.post<IPdfConverterConversion>(`${environment.API_URL}/statements/conversions`, data)
    .pipe(
      tap((conversion) => this.registerConversion(conversion, true)),
    );
  }

  get(request: IPdfConverterConversionsRequest = {}): Observable<IPdfConverterConversion[]> {
    return this.getConversions(request).pipe(
      tap((items) => items.forEach((item) => this.registerConversion(item, true))),
    );
  }

  getConversions(request: IPdfConverterConversionsRequest = {}): Observable<IPdfConverterConversion[]> {
    return this.http.get<IPdfConverterConversion[]>(
      `${environment.API_URL}/statements/conversions`,
      getHttpParams(request),
    );
  }

  getConversionById(id: number): Observable<IPdfConverterConversion> {
    return this.http.get<IPdfConverterConversion>(`${environment.API_URL}/statements/conversions/${id}`);
  }

  retryConversion(id: number): Observable<IPdfConverterConversion> {
    return this.http.post<IPdfConverterConversion>(`${environment.API_URL}/statements/conversions/${id}/retry`, {})
    .pipe(
      tap((conversion) => this.registerConversion(conversion, true)),
    );
  }

  deleteConversion(id: number): Observable<void> {
    return this.http.delete<void>(`${environment.API_URL}/statements/conversions/${id}`)
    .pipe(
      tap(() => this.removeConversion(id)),
    );
  }

  exportConversion(id: number, format: TPdfConverterExportFormat): Observable<HttpResponse<Blob>> {
    return this.http.post(
      `${environment.API_URL}/statements/conversions/${id}/export/${format}`,
      {},
      {
        responseType: 'blob' as const,
        observe: 'response' as const,
      },
    );
  }

  loadActiveConversions(): Observable<IPdfConverterConversion[]> {
    return this.getConversions({status: [this.activeStatus]}).pipe(
      tap((items) => items.forEach((item) => this.registerConversion(item, true))),
      catchError(() => of([])),
    );
  }

  addToQueue(id: number) {
    if (!id || this.queue.includes(id)) {
      return;
    }
    this.queue.push(id);
  }

  isActiveStatus(status: TPdfConverterConversionStatus): boolean {
    return status === this.activeStatus;
  }

  getActiveConversionsSnapshot(): IPdfConverterConversion[] {
    return this.activeConversionsSubject.value;
  }

  setActionPending(conversionId: number, pending: boolean) {
    if (!conversionId) {
      return;
    }

    if (pending) {
      this.pendingActionsMap.set(conversionId, true);
      return;
    }

    this.pendingActionsMap.delete(conversionId);
  }

  isActionPending(conversionId: number): boolean {
    return !!conversionId && this.pendingActionsMap.get(conversionId) === true;
  }

  private startQueue() {
    if (this.queueInterval) {
      return;
    }

    this.queueInterval = setInterval(() => {
      if (!this.queue.length || this.statusIsUpdating) {
        return;
      }

      const conversionId = this.queue.shift();
      if (!conversionId) {
        return;
      }

      this.statusIsUpdating = true;
      this.getConversionById(conversionId)
      .pipe(
        finalize(() => this.statusIsUpdating = false),
      )
      .subscribe({
        next: (conversion) => {
          this.registerConversion(conversion, false);
          if (this.isActiveStatus(conversion.status)) {
            this.addToQueue(conversion.id);
          }
        },
        error: () => {
          if (this.conversionMap.has(conversionId)) {
            this.addToQueue(conversionId);
          }
        },
      });
    }, 5000);
  }

  private registerConversion(conversion: IPdfConverterConversion, enqueueIfActive: boolean) {
    if (!conversion?.id) {
      return;
    }

    this.conversionMap.set(conversion.id, conversion);
    this.conversionChanged.emit(conversion);
    this.updateActiveConversions();

    if (enqueueIfActive && this.isActiveStatus(conversion.status)) {
      this.addToQueue(conversion.id);
    }
  }

  private removeConversion(id: number) {
    if (!id) {
      return;
    }

    this.conversionMap.delete(id);
    this.pendingActionsMap.delete(id);
    let queueIndex = this.queue.indexOf(id);
    while (queueIndex !== -1) {
      this.queue.splice(queueIndex, 1);
      queueIndex = this.queue.indexOf(id);
    }

    this.conversionDeleted.emit(id);
    this.updateActiveConversions();
  }

  private updateActiveConversions() {
    const active = Array.from(this.conversionMap.values())
    .filter((item) => this.isActiveStatus(item.status))
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    this.activeConversionsSubject.next(active);
  }

}
